import { MultiSelectFilter } from '@components/MultiSelectFilter';
import { Typography, List, ListItemText, Divider, Link } from '@mui/material';
import React, { useMemo, type Dispatch, type SetStateAction, useState } from 'react';
import { getCutCounts, getPossibleStatusChanges, validateCutStatusChanges } from '@utils/cut';
import { useAlert } from '@hooks/useAlert';
import { ConfirmModal } from '@components/dialogs/ConfirmModal';
import { updateStatus } from '@api/cut';
import { Virtualize } from '@components/Virtualize';
import { useSelector } from 'react-redux';

import type { CutStatus } from '@consts/cut';
import type { RoleUtils } from '@/types/permissions';
import type { RootState } from '@features/redux/store';
import type { ParsedCut } from '@/types/cuts';
import { TooltipButton } from '@components/buttons/TooltipButton';

const StatusFilter = ({
    status,
    setStatus,
    options,
}: {
    status: CutStatus | '';
    setStatus: Dispatch<SetStateAction<CutStatus | ''>>;
    options: CutStatus[];
}) => {
    return (
        <>
            <Typography>Action:</Typography>
            <MultiSelectFilter
                sort={false}
                label='Status'
                options={options}
                getLabelValue={(option) => option}
                value={{
                    [status]: Boolean(status),
                }}
                onChange={(val) => {
                    setStatus((prev) => {
                        // ? handle de-selection
                        if (prev === val) return '';
                        return val as CutStatus;
                    });
                }}
            />
        </>
    );
};

const CutActionModal = ({
    onConfirm,
    showModal,
    setShowModal,
    selectedCuts,
    setSelectedCuts,
    status,
    role,
    selectedCount,
}: {
    onConfirm: (items: { cutIds: string[]; status: CutStatus }) => void;
    showModal: boolean;
    setShowModal: Dispatch<SetStateAction<boolean>>;
    selectedCuts: Record<string, ParsedCut>;
    setSelectedCuts: Dispatch<SetStateAction<Record<string, ParsedCut>>>;
    status: CutStatus | '';
    role: RoleUtils;
    selectedCount: number;
}) => {
    // Ensure that the modal automatically closes if the number of selected
    // items is zero
    if (!selectedCount) setShowModal(false);
    const [modalRoot, setModalRoot] = useState<HTMLElement | null>(null);

    const counts = useMemo(() => getCutCounts(selectedCuts), [selectedCuts]);

    const { readyToPatch, failedValidation, groupedFailedItems } = useMemo(() => {
        return validateCutStatusChanges({ status, role, selectedCuts });
    }, [status, selectedCuts]);

    const handleClick = () => {
        // eslint-disable-next-line @typescript-eslint/no-unused-vars
        const badIds = groupedFailedItems.flatMap(([_, ids]) => ids);

        // Remove all invalid changes from selection
        setSelectedCuts((prev) => {
            return Object.fromEntries(
                Object.entries(prev).filter(([cutId]) => {
                    return !badIds.includes(cutId);
                })
            );
        });
    };

    return (
        <ConfirmModal
            disabled={Boolean(failedValidation.length)}
            title='Confirm cut status change(s)'
            open={showModal}
            setOpen={setShowModal}
            onConfirm={() => {
                if (!status || Boolean(failedValidation.length)) return;
                onConfirm(readyToPatch);
            }}>
            {groupedFailedItems.length ? (
                <>
                    <Typography>
                        Unable to change the status of some cuts. Click{' '}
                        <Link sx={{ cursor: 'pointer' }} onClick={handleClick}>
                            here
                        </Link>{' '}
                        to remove invalid cut selections.
                    </Typography>
                    {groupedFailedItems.map(([oldStatus, cutIds]) => {
                        const text = `Cannot change from "${oldStatus}" to "${status}"`;

                        return (
                            <React.Fragment key={`${oldStatus}-failed-validation`}>
                                <Typography color='red' sx={{ pl: 4 }}>
                                    {text}
                                </Typography>
                                {cutIds.map((id) => (
                                    <Typography color='red' key={`${oldStatus}-${id}-failed-validation`} sx={{ pl: 8 }}>
                                        {id}
                                    </Typography>
                                ))}
                            </React.Fragment>
                        );
                    })}
                </>
            ) : (
                <>
                    <Typography>
                        Are you sure you want to change the status of the following cut(s) to {status}?
                    </Typography>
                    <List ref={setModalRoot} sx={{ maxHeight: '400px', overflowY: 'scroll' }}>
                        {Object.entries(counts).map(([company, data]) => {
                            const locations = Object.entries(data);

                            return (
                                <Virtualize
                                    root={modalRoot}
                                    key={`${company}-modal-item`}
                                    height={locations.length * 40}>
                                    <Divider />
                                    <ListItemText primary={`${company}:`} />
                                    {locations.map(([location, data]) => {
                                        const cuts = Object.entries(data);
                                        const info = cuts.map(([type, count]) => `${count} ${type}(s)`).join(', ');
                                        return (
                                            <ListItemText
                                                key={`${company}-${location}-modal-item`}
                                                primary={location}
                                                secondary={info}
                                                sx={{ pl: 4 }}
                                            />
                                        );
                                    })}
                                </Virtualize>
                            );
                        })}
                    </List>
                </>
            )}
        </ConfirmModal>
    );
};

type CutActionProps = {
    status: CutStatus | '';
    setStatus: Dispatch<SetStateAction<CutStatus | ''>>;
    selectedCuts: Record<string, ParsedCut>;
    setSelectedCuts: Dispatch<SetStateAction<Record<string, ParsedCut>>>;
    role: RoleUtils;
    onActionSuccess: () => void;
};

export const CutAction = ({
    status,
    setStatus,
    selectedCuts,
    setSelectedCuts,
    role,
    onActionSuccess,
}: CutActionProps) => {
    const { setAlert } = useAlert();
    const { IdToken } = useSelector((state: RootState) => state.auth);
    const [modalOpen, setModalOpen] = useState(false);

    const options = useMemo(() => getPossibleStatusChanges(role), [role]);

    const selectedCount = Object.keys(selectedCuts).length;

    const handleConfirm = async (items: { cutIds: string[]; status: CutStatus }) => {
        try {
            const modified = await updateStatus({
                token: IdToken!,
                body: items,
            });

            setAlert(
                `${modified.length} of ${selectedCount} changes succeeded${
                    selectedCount !== modified.length
                        ? `, ${selectedCount - modified.length} changes from "${status}" to "${status}" ignored`
                        : ''
                }.`,
                'success'
            );
            onActionSuccess();
            setSelectedCuts({});
            setStatus('');
        } catch (error) {
            setAlert(`${selectedCount} of ${selectedCount} changes failed.`, 'error');
        }
    };

    return (
        <>
            {!options.length ? null : (
                <div style={{ flexGrow: 1, display: 'flex', alignItems: 'center', gap: '5px', flexWrap: 'wrap' }}>
                    <StatusFilter status={status} setStatus={setStatus} options={options} />
                    <TooltipButton
                        title={'Perform an action on the selected cut(s)'}
                        onClick={() => setModalOpen(true)}
                        disabled={!selectedCount || !status}>
                        Save
                    </TooltipButton>
                    <CutActionModal
                        selectedCount={selectedCount}
                        onConfirm={handleConfirm}
                        status={status}
                        showModal={modalOpen}
                        setShowModal={setModalOpen}
                        role={role}
                        selectedCuts={selectedCuts}
                        setSelectedCuts={setSelectedCuts}
                    />
                </div>
            )}
        </>
    );
};
